import React from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Activity,
  TrendingUp,
  FileText,
  LucideIcon,
  Shield,
  Clock,
} from "lucide-react";
import PatientFlowChart from "../../../components/patientFlowChart";
import generateMeta from "../../../shared/services/metadataService";
import metaData from "../../../config/metaData";

interface KeyMetric {
  title: string;
  value: string;
  subtitle: string;
  icon: LucideIcon;
  color: "primary" | "green" | "orange" | "red";
}

interface PatientFlowData {
  month: string;
  total: number;
}

interface ClinicalMetric {
  title: string;
  value: string;
  status: "excellent" | "good" | "normal" | "warning" | "urgent";
  icon: LucideIcon;
  trend: string;
}

interface ActivityType {
  id: number;
  time: string;
  patient: string;
  action: string;
  department: string;
  priority: "urgent" | "normal" | "routine";
}

/**
 * EHR Dashboard Home component that displays patient stats, activities, and charts.
 * @returns {JSX.Element} The Home Dashboard component JSX.
 */
const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const meta = generateMeta(metaData.home);

  // Key metrics data
  const keyMetrics: KeyMetric[] = [
    {
      title: t("dashboard.stats.aiQueries"),
      value: "847",
      subtitle: t("dashboard.stats.queriesToday"),
      icon: Activity,
      color: "primary",
    },
    {
      title: t("dashboard.stats.suggestionAcceptance"),
      value: "92%",
      subtitle: t("dashboard.stats.acceptanceRate"),
      icon: TrendingUp,
      color: "green",
    },
    {
      title: t("dashboard.stats.timeSaved"),
      value: "3.2h",
      subtitle: t("dashboard.stats.perClinician"),
      icon: Clock,
      color: "orange",
    },
    {
      title: t("dashboard.stats.weeklyValue"),
      value: "$45K",
      subtitle: t("dashboard.stats.totalValue"),
      icon: TrendingUp,
      color: "primary",
    },
  ];

  // Patient flow data
  const patientFlowData: PatientFlowData[] = [
    { month: "Jan", total: 285 },
    { month: "Feb", total: 321 },
    { month: "Mar", total: 298 },
    { month: "Apr", total: 342 },
    { month: "May", total: 367 },
    { month: "Jun", total: 389 },
    { month: "Jul", total: 412 },
    { month: "Aug", total: 394 },
    { month: "Sep", total: 378 },
    { month: "Oct", total: 405 },
    { month: "Nov", total: 499 },
    { month: "Dec", total: 401 },
  ];

  // Clinical performance data
  const clinicalMetrics: ClinicalMetric[] = [
    {
      title: t("dashboard.performance.guidelineAdherence"),
      value: "34%",
      status: "excellent",
      icon: Shield,
      trend: "+34%",
    },
    {
      title: t("dashboard.performance.pendingReports"),
      value: "24",
      status: "normal",
      icon: FileText,
      trend: "pending",
    },
    {
      title: t("dashboard.performance.activeTasks"),
      value: "12",
      status: "good",
      icon: Activity,
      trend: "active",
    },
    {
      title: t("dashboard.performance.criticalAlerts"),
      value: "3",
      status: "warning",
      icon: AlertTriangle,
      trend: "critical",
    },
  ];

  // Recent patient activities
  const recentActivities: ActivityType[] = [
    {
      id: 1,
      time: "09:30",
      patient: "Sarah Johnson",
      action: t("dashboard.activities.consultationCompleted"),
      department: t("dashboard.departments.cardiology"),
      priority: "routine",
    },
    {
      id: 2,
      time: "09:45",
      patient: "Michael Chen",
      action: t("dashboard.activities.labResultsAvailable"),
      department: t("dashboard.departments.laboratory"),
      priority: "normal",
    },
    {
      id: 3,
      time: "10:15",
      patient: "Emma Davis",
      action: t("dashboard.activities.prescriptionIssued"),
      department: t("dashboard.departments.pharmacy"),
      priority: "normal",
    },
    {
      id: 4,
      time: "10:30",
      patient: "James Wilson",
      action: t("dashboard.activities.emergencyAdmission"),
      department: t("dashboard.departments.emergency"),
      priority: "urgent",
    },
  ];

  const getColorClasses = (color: KeyMetric["color"]) => {
    const colors = {
      primary: "bg-primary-50 dark:bg-primary/10 text-primary-600 dark:text-primary-400 border-primary-100 dark:border-primary-500/20",
      green:
        "bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 border-green-100 dark:border-green-500/20",
      orange:
        "bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-100 dark:border-orange-500/20",
      red: "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-100 dark:border-red-500/20",
    };
    return colors[color] || colors.primary;
  };

  const getStatusColor = (status: ClinicalMetric["status"]) => {
    switch (status) {
      case "excellent":
        return "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10";
      case "good":
        return "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary/10";
      case "normal":
        return "text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-500/10";
      case "warning":
        return "text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-500/10";
      case "urgent":
        return "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10";
      default:
        return "text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-500/10";
    }
  };

  const getPriorityColor = (priority: ActivityType["priority"]) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-400";
      case "normal":
        return "bg-primary-100 dark:bg-primary/10 text-primary-700 dark:text-primary-400";
      case "routine":
        return "bg-gray-100 dark:bg-gray-500/10 text-gray-700 dark:text-gray-400";
      default:
        return "bg-gray-100 dark:bg-gray-500/10 text-gray-700 dark:text-gray-400";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      {meta}
      <div className="py-4 mx-auto space-y-4 sm:space-y-6 lg:space-y-8 xl:space-y-10">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {keyMetrics.map((metric, index) => {
            const IconComponent = metric.icon;
            return (
              <div
                key={index}
                className="bg-white dark:bg-[#1a1b1c] border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 lg:p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm lg:text-base font-medium text-gray-600 dark:text-gray-400 truncate">
                      {metric.title}
                    </p>
                    <p className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 dark:text-white mt-1">
                      {metric.value}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 mt-1 truncate">
                      {metric.subtitle}
                    </p>
                  </div>
                  <div
                    className={`p-2 sm:p-3 lg:p-4 rounded-lg ${getColorClasses(metric.color)} ml-2 sm:ml-3`}
                  >
                    <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Patient Activities */}
          <div className="bg-white dark:bg-[#1a1b1c] border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center space-x-2">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-gray-600 dark:text-gray-400" />
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white">
                  {t("dashboard.activities.title")}
                </h3>
              </div>
            </div>
            <div className="p-3 sm:p-4 lg:p-6">
              <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                {recentActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-lg border border-gray-100 dark:border-gray-800"
                  >
                    <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1 mb-2 sm:mb-0">
                      <div className="text-xs sm:text-sm font-mono text-gray-500 dark:text-gray-400 w-12 sm:w-16 flex-shrink-0">
                        {activity.time}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 dark:text-white text-sm sm:text-base lg:text-lg truncate">
                          {activity.patient}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                          {activity.action}
                        </p>
                      </div>
                    </div>
                    <div className="text-left sm:text-right space-y-1 flex-shrink-0">
                      <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                        {activity.department}
                      </div>
                      <span
                        className={`inline-block px-2 py-1 text-xs sm:text-sm font-medium rounded ${getPriorityColor(activity.priority)}`}
                      >
                        {activity.priority}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Clinical Performance */}
          <div className="bg-white dark:bg-[#1a1b1c] border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-gray-600 dark:text-gray-400" />
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white">
                  {t("dashboard.performance.title")}
                </h3>
              </div>
            </div>
            <div className="p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4">
              {clinicalMetrics.map((metric, index) => {
                const IconComponent = metric.icon;
                return (
                  <div
                    key={index}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-lg border border-gray-100 dark:border-gray-800"
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1 mb-2 sm:mb-0">
                      <div
                        className={`p-2 sm:p-3 rounded-lg ${getStatusColor(metric.status)} flex-shrink-0`}
                      >
                        <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white truncate">
                          {metric.title}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                          {metric.trend} {t("dashboard.performance.thisMonth")}
                        </p>
                      </div>
                    </div>
                    <div className="text-left sm:text-right flex-shrink-0">
                      <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">
                        {metric.value}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Patient Flow Chart */}
        <div className="bg-white dark:bg-[#1a1b1c] border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-gray-600 dark:text-gray-400" />
              <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white">
                {t("dashboard.chart.title")}
              </h3>
            </div>
          </div>
          <div className="p-3 sm:p-4 lg:p-6 h-80">
            <PatientFlowChart data={patientFlowData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
